/* eslint-disable max-lines-per-function */
import { QueryClient, useQuery } from 'react-query'
import { useRouter } from 'next/router'
import merge from 'lodash/merge'
import { useState } from 'react'
import { ICategoryFnO } from '../../FnO/types/options-chain'
import { TAssetDetailsData } from '../../Widgets/types'
import { TVyaparMPINError } from '../../types/TGETINStocksBuy'
import StockDetailLoggedOutV1 from '../InStockDetailPageV2/StockDetailLoggedOutV1'
import { FUTURES } from '../../FnO/constants'
import { IINDetailsPage, TLivePrice } from './types'
import { LivePriceContext, SetLivePriceContext } from './context'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'
import { getInitData } from '@/root/utils/network-adapter/useQuery-helpers'
import { IND_STOCKS_CATELOGUE } from '@/root/api/stocks/apiConstants'
import SpinnerLoader from '@/components/Common/header/spinner-loader'
import SomethingWentWrong from '@/components/Common/SomethingWentWrong'
import SEODetail from '@/components/INDStocks/DetailPage/SEODetail'
import ETFDetail from '@/components/INDStocks/DetailPage/StockETF'
import { indianStocksBackgroundApiBaseUrl } from '@/root/api/apiConstants'
import { TStrapiV4PageType } from '@/stories/strapi-4/common-types'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import { GreenButton } from '@/components/NewHomepage/Button'
import { addQueryParamsToApiV2 } from '@/root/helpers'
import ScrollAwareTopNav from '@/components/ScrollAwareV2/ScrollAwareV2'
import { getNavItems } from '@/root/utils/stocks/getNavItems'
import { getETFNavItems } from '@/root/utils/stocks/getETFNavItems'
import { TRelatedBlogs } from '@/root/utils/articles/types'

const INStockDetail = ({
  headers,
  data: serverData,
  fnoApiData,
  errorStatusCode,
  isMobile,
  host,
  strapiApiResponse,
  inStocksRelatedBlogs,
}: IINDetailsPage & {
  strapiApiResponse: TStrapiV4PageType | null
  fnoApiData: ICategoryFnO | null
  inStocksRelatedBlogs: TRelatedBlogs | null
}) => {
  const [livePrice, setLivePrice] = useState<TLivePrice | null>(null)

  const router = useRouter()
  const { query = {} } = router
  const { stockSlug = '', scrip_asset_type } = query as {
    stockSlug: string
    scrip_asset_type: string
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  })

  const { data, isLoading, isError } = useQuery<TAssetDetailsData>(
    `indianstocksDetails${stockSlug}`,
    () =>
      fetchRequest({
        config: {
          headers,
        },
        timeout: 0,
        url: addQueryParamsToApiV2(
          `${indianStocksBackgroundApiBaseUrl}${IND_STOCKS_CATELOGUE}${stockSlug}`,
          {
            response_format: 'web',
            'catalog-required': 'true',
            'graph-optional': 'true',
            ...(scrip_asset_type && { 'scrip-asset-type': scrip_asset_type }),
          },
        ),
      }),
    {
      retry: 0,
      staleTime: 300,
      enabled: errorStatusCode !== 200,
      ...getInitData(errorStatusCode === 200 ? serverData : {}),
      onError(err: TVyaparMPINError) {
        if (err.data?.navlink?.web) {
          const url = new URL(err.data.navlink.web)
          const search = new URLSearchParams(url.search)
          const currUrl = new URL(window.location.href)
          search.append(
            'return_to',
            encodeURIComponent(currUrl.pathname + currUrl.search).substring(
              0,
              1000,
            ),
          )
          void queryClient.removeQueries(`indianstocksDetails${stockSlug}`)
          void router.push({
            pathname: url.pathname,
            search: search.toString(),
          })
        }
      },
    },
  )

  const { data: stockApiData, isLoading: stockApiLoading } =
    useQuery<TAssetDetailsData>(
      `catalog${stockSlug}`,
      () =>
        fetchRequest({
          config: {
            headers,
          },
          timeout: 0,
          url: addQueryParamsToApiV2(
            `${indianStocksBackgroundApiBaseUrl}${IND_STOCKS_CATELOGUE}${stockSlug}`,
            {
              response_format: 'web',
              'catalog-required': 'true',
              'investment-optional': 'true',
            },
          ),
        }),
      {
        retry: 0,
        staleTime: 300,
        enabled: !!data?.catalog?.entity_basic?.company_code,
      },
    )

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <SpinnerLoader />
      </div>
    )
  }

  if (isError) {
    return (
      <section className="relative mb-36 mt-4 bg-white">
        <div className="lg:min-w-4/5 lg:flex lg:max-w-full lg:flex-col lg:items-center lg:justify-center">
          <SomethingWentWrong />
        </div>
      </section>
    )
  }

  const combineData = merge(data, stockApiData)

  const isETF = combineData?.catalog?.entity_class === 'ETF'

  const categoryData =
    fnoApiData?.catalog?.stock_details ?? fnoApiData?.catalog?.index_details

  const futures = categoryData?.categories?.find(
    item => item?.category_code?.toUpperCase()?.includes(FUTURES),
  )

  const hasFNO = categoryData?.categories?.some(
    category => category?.stocks?.length,
  )

  const showFNO = !!hasFNO || !!futures?.stocks?.length

  const navItems = isETF
    ? getETFNavItems({
        etf_details: combineData?.catalog?.etf_details,
      })
    : getNavItems({
        stock_details: combineData?.catalog?.stock_details,
        entity_stats: combineData?.catalog?.entity_stats,
        showFNO,
        inStocksRelatedBlogs,
      })

  const strapiSeoData = strapiApiResponse?.data.attributes.seo
  let stockDetail = (
    <StockDetailLoggedOutV1
      {...combineData}
      stockApiLoading={stockApiLoading}
      isMobile={isMobile}
      headers={headers}
      strapiApiResponse={strapiApiResponse}
      fnoApiData={fnoApiData}
      inStocksRelatedBlogs={inStocksRelatedBlogs}
      showFNO={showFNO}
    />
  )
  const basicData = combineData?.catalog?.entity_basic
  const statsData = combineData?.catalog?.entity_stats
  const schemaData = {
    symbol: basicData?.symbol,
    logo: basicData?.logo,
    name: basicData?.name,
    price: statsData?.live_price,
    daysHigh: statsData?.day_high,
    daysLow: statsData?.day_low,
    fiftyTwoWeekHigh: statsData?.['52week_high'],
    fiftyTwoWeekLow: statsData?.['52week_low'],
  }
  switch (combineData?.catalog?.entity_class) {
    case 'ETF':
      stockDetail = (
        <ETFDetail
          {...combineData}
          stockApiLoading={stockApiLoading}
          isMobile={isMobile}
          headers={headers}
        />
      )
      break

    default:
      break
  }
  return (
    <LivePriceContext.Provider value={livePrice}>
      <SetLivePriceContext.Provider value={setLivePrice}>
        <ScrollAwareTopNav navItems={navItems} isMobile={!!isMobile} />
        <div className="herolike-wrapper flex flex-col items-center">
          <div className="relative flex w-full flex-row pt-2">
            <SEODetail
              {...combineData?.catalog?.entity_basic}
              entity_class={combineData?.catalog?.entity_class}
              host={host}
              stockSchemaData={schemaData}
              useShortName={true}
              strapiSeoData={strapiSeoData}
            />
            <StencilCDNImage
              src="https://indcdn.indmoney.com/cdn/images/fe/indstocks-logo.png"
              alt="INDstocks by INDmoney"
              width={80}
              height={33}
              className="hidden lg:flex"
            />
          </div>
          <div className="mt-6 flex w-full xl:mt-8">{stockDetail}</div>
        </div>
        <div className="sticky bottom-0 z-40 lg:hidden">
          <GreenButton
            btnText="Open Free Demat Account Now"
            addclassName="!rounded-none"
          />
        </div>
      </SetLivePriceContext.Provider>
    </LivePriceContext.Provider>
  )
}

export default INStockDetail
